import React from 'react';
import OnboardingTopBar from '@/components/onboarding/OnboardingTopBar';
import OnboardingSection from '@/components/onboarding/OnboardingSection';
import InputOtp from '@/components/ui/InputOtp.jsx';
import supabase from '@/services/supabaseClient';
import { useLanguage } from '@/contexts/LanguageContext.jsx';
import { verificationRequest, saveVerificationPhone, resolvePhoneVerificationState } from '@/features/phone-verification/phoneVerificationService.js';
import usePhoneVerificationCapabilities from '@/features/phone-verification/usePhoneVerificationCapabilities.js';

export default function StepOtp({ step, total, phone, profileTable = 'patient_profiles', onBack, onContinue }) {
  const { t, languageCode } = useLanguage();
  const role = profileTable === 'doctor_profiles' ? 'doctor' : 'patient';
  const [profile, setProfile] = React.useState(null);
  const [ready, setReady] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState('');
  const [cooldown, setCooldown] = React.useState(0);
  const p = String(phone || profile?.phone || '').trim();
  const capabilities = usePhoneVerificationCapabilities(p);
  const state = resolvePhoneVerificationState({ phone: p, savedPhone: profile?.phone,
    verifiedAt: profile?.phone_verified_at, capabilities, pending: sent });
  React.useEffect(() => {
    let alive = true;
    setReady(false);
    supabase.auth.getUser().then(async ({ data, error }) => {
      if (error || !data.user) throw new Error('session_unavailable');
      const { data: row, error: readError } = await supabase.from(profileTable).select('phone,phone_verified_at').eq('user_id', data.user.id).maybeSingle();
      if (readError) throw readError;
      if (alive) { setProfile({ ...row, user_id: data.user.id }); setReady(true); }
    }).catch(() => { if (alive) setError(t('onboarding.otp.sessionNotReady', 'Your session is not ready. Please try again.')); });
    return () => { alive = false; };
  }, [phone, profileTable]);
  React.useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(n => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);
  async function send(channel) {
    if (busy || cooldown || !ready || !capabilities?.channels.includes(channel) || state.verified) return;
    setBusy(true); setError('');
    try {
      await saveVerificationPhone({ phone: p, role, userId: profile.user_id });
      await verificationRequest('send', { phone: p, role, channel, languageCode });
      setProfile(current => ({ ...current, phone: p })); setSent(true); setCode(''); setCooldown(60);
    } catch { setError(t('onboarding.otp.sendFailed', 'We could not send a verification code. Please try again.')); }
    finally { setBusy(false); }
  }
  async function verify(next = code) {
    const clean = String(next).replace(/\D/g, '').slice(0, 6);
    setCode(clean);
    if (busy || !sent || clean.length !== 6) return;
    setBusy(true); setError('');
    try {
      await verificationRequest('verify', { phone: p, code: clean, role });
      const { data: row, error } = await supabase.from(profileTable).select('phone,phone_verified_at').eq('user_id', profile.user_id).single();
      if (error || row.phone !== p || !row.phone_verified_at) throw new Error('verification_not_persisted');
      setProfile(current => ({ ...current, ...row })); onContinue?.();
    } catch { setError(t('onboarding.otp.invalidCode', 'That code did not verify. Please check it and try again.')); }
    finally { setBusy(false); }
  }
  const french = String(languageCode).startsWith('fr');
  return <div className="min-h-dvh flex flex-col">
    <OnboardingTopBar step={step} total={total} onBack={onBack} />
    <OnboardingSection header={<h2 className="text-title-large text-on-surface">{t('onboarding.otp.title', 'Phone verification')}</h2>}>
      {state.verified ? <p>{t('settings.edit.phone.verified', 'This phone number is already verified.')}</p> :
        !ready || !capabilities ? <p>{t('common.loading', 'Loading...')}</p> : !state.available ?
          <p>{t('settings.edit.phone.deliveryUnavailableSaved', 'Your phone number is saved. Verification delivery is temporarily unavailable.')}</p> :
          <><p>{french ? 'Choisissez un canal disponible pour vérifier votre téléphone.' : 'Choose an available channel to verify your phone.'}</p>
            <div className="mt-4 flex items-center gap-3">{capabilities.channels.map(channel =>
              <button key={channel} type="button" disabled={busy || cooldown > 0} onClick={() => send(channel)} className="text-label-large text-primary disabled:opacity-50">
                {channel === 'whatsapp' ? (french ? 'Envoyer le code par WhatsApp' : 'Send code with WhatsApp') : (french ? 'Envoyer par SMS' : 'Send code by SMS')}
              </button>)}{cooldown > 0 && <span>{cooldown}s</span>}</div>
            {sent && <div className="mt-4"><p>{t('onboarding.otp.requestAccepted', 'Verification request submitted.')}</p>
              <InputOtp value={code} onChange={setCode} onComplete={verify} tone="surface" align="center" autoFocus /></div>}
          </>}
      {(state.verified || (ready && capabilities && !state.available)) && <button type="button" onClick={onContinue} className="mt-4 text-label-large text-primary">{t('common.continue', 'Continue')}</button>}
      {error && <p role="alert" className="mt-3 text-[rgb(var(--afrimed-danger))]">{error}</p>}
    </OnboardingSection>
  </div>;
}
